/**
 * Super Admin - Packages Management
 * Create and manage packages with calendar date selection
 */

import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React examples
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Calendar component
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

const localizer = momentLocalizer(moment);

// API
import { adminAPI } from "utils/api";

function PackagesManagement() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [formData, setFormData] = useState({
    nameEn: "",
    descriptionEn: "",
    price: "",
    packageType: "Custom",
    discountPercentage: "0",
    isActive: true,
    properties: [],
  });
  const [propertiesList, setPropertiesList] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingPackage, setLoadingPackage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState("all");
  const [allPackages, setAllPackages] = useState([]); // Store all packages for analytics

  useEffect(() => {
    fetchPackages();
  }, [statusFilter]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (statusFilter !== "all") {
        filters.isActive = statusFilter === "active" ? "true" : "false";
      }
      const response = await adminAPI.getAllPackages(filters);
      const packagesData = response.data || response.packages || [];

      // If filtering, update filtered list; otherwise store all for analytics
      if (statusFilter === "all") {
        setAllPackages(packagesData);
        setPackages(packagesData);
      } else {
        setPackages(packagesData);
        // Fetch all for analytics if we don't have them
        if (allPackages.length === 0) {
          const allResponse = await adminAPI.getAllPackages();
          setAllPackages(allResponse.data || allResponse.packages || []);
        }
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      setMessage({ type: "error", text: "Failed to load packages" });
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      setLoadingProperties(true);
      const response = await adminAPI.getAllProperties({ status: "Active" });
      const list = response.data || response.properties || [];
      setPropertiesList(list);
    } catch (err) {
      console.error("Error fetching properties:", err);
      setPropertiesList([]);
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleOpenDialog = async (pkg = null) => {
    setDialogOpen(true);
    setMessage({ type: "", text: "" });
    fetchProperties();

    if (pkg) {
      const packageId = pkg._id || pkg.id;
      setEditingPackage({ _id: packageId });
      setLoadingPackage(true);
      setFormData({
        nameEn: "",
        descriptionEn: "",
        price: "",
        packageType: "Custom",
        discountPercentage: "0",
        isActive: true,
        properties: [],
      });
      setSelectedDates([]);

      try {
        const response = await adminAPI.getPackageById(packageId);
        const fullPackage = response.data || response.package || response;
        if (!fullPackage) {
          setMessage({ type: "error", text: "Failed to load package details" });
          setLoadingPackage(false);
          return;
        }
        const propertyIds = (fullPackage.properties || [])
          .map((prop) => (typeof prop === "string" ? prop : prop._id || prop.id))
          .filter(Boolean);
        setFormData({
          nameEn: fullPackage.name?.en || "",
          descriptionEn: fullPackage.description?.en || "",
          price: String(fullPackage.price ?? ""),
          packageType: fullPackage.packageType || "Custom",
          discountPercentage: String(fullPackage.discountPercentage ?? 0),
          isActive: fullPackage.isActive !== undefined ? fullPackage.isActive : true,
          properties: propertyIds,
        });
        setSelectedDates((fullPackage.selectedDates || []).map((d) => new Date(d)));
        setEditingPackage(fullPackage);
      } catch (err) {
        console.error("Error fetching package:", err);
        setMessage({ type: "error", text: err.message || "Failed to load package details" });
      } finally {
        setLoadingPackage(false);
      }
    } else {
      setEditingPackage(null);
      setLoadingPackage(false);
      setFormData({
        nameEn: "",
        descriptionEn: "",
        price: "",
        packageType: "Custom",
        discountPercentage: "0",
        isActive: true,
        properties: [],
      });
      setSelectedDates([]);
    }
  };

  const handleCloseDialog = () => {
    if (!submitting) {
      setDialogOpen(false);
      setEditingPackage(null);
      setLoadingPackage(false);
      setFormData({
        nameEn: "",
        descriptionEn: "",
        price: "",
        packageType: "Custom",
        discountPercentage: "0",
        isActive: true,
        properties: [],
      });
      setSelectedDates([]);
    }
    setMessage({ type: "", text: "" });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle date selection from calendar
  const handleSelectSlot = ({ start, end }) => {
    const selectedDate = new Date(start);
    selectedDate.setHours(0, 0, 0, 0);

    // Check if date is already selected
    const dateStr = selectedDate.toISOString().split("T")[0];
    const isSelected = selectedDates.some((d) => {
      const dStr = new Date(d).toISOString().split("T")[0];
      return dStr === dateStr;
    });

    if (isSelected) {
      // Remove date if already selected
      setSelectedDates((prev) =>
        prev.filter((d) => {
          const dStr = new Date(d).toISOString().split("T")[0];
          return dStr !== dateStr;
        })
      );
    } else {
      // Add date if not selected
      setSelectedDates((prev) => [...prev, selectedDate].sort((a, b) => a - b));
    }
  };

  // Style selected dates in calendar
  const eventStyleGetter = (event) => {
    const isSelected = selectedDates.some((d) => {
      const dStr = new Date(d).toISOString().split("T")[0];
      const eStr = new Date(event.start).toISOString().split("T")[0];
      return dStr === eStr;
    });

    return {
      style: {
        backgroundColor: isSelected ? "#2196F3" : "#4CAF50",
        borderRadius: "5px",
        opacity: 0.8,
        color: "white",
        border: "0px",
        display: "block",
      },
    };
  };

  // Create events for selected dates
  const calendarEvents = selectedDates.map((date, index) => ({
    id: index,
    title: "Selected",
    start: date,
    end: new Date(date.getTime() + 24 * 60 * 60 * 1000), // Next day
  }));

  const handleSave = async () => {
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      // Validation
      if (!formData.nameEn.trim()) {
        setMessage({ type: "error", text: "Package name is required" });
        setSubmitting(false);
        return;
      }

      if (!formData.price || parseFloat(formData.price) < 0) {
        setMessage({ type: "error", text: "Valid price is required" });
        setSubmitting(false);
        return;
      }

      if (selectedDates.length === 0) {
        setMessage({ type: "error", text: "Please select at least one date for the package" });
        setSubmitting(false);
        return;
      }

      const packageData = {
        nameEn: formData.nameEn.trim(),
        nameAr: formData.nameEn.trim(), // Use English name for Arabic as well
        descriptionEn: formData.descriptionEn.trim(),
        descriptionAr: formData.descriptionEn.trim(), // Use English description for Arabic as well
        price: parseFloat(formData.price),
        selectedDates: selectedDates.map((d) => d.toISOString()),
        packageType: formData.packageType,
        discountPercentage: parseFloat(formData.discountPercentage) || 0,
        isActive: formData.isActive,
        properties: Array.isArray(formData.properties) ? formData.properties : [],
      };

      if (editingPackage) {
        await adminAPI.updatePackage(editingPackage._id, packageData);
        setMessage({ type: "success", text: "Package updated successfully" });
      } else {
        await adminAPI.createPackage(packageData);
        setMessage({ type: "success", text: "Package created successfully" });
      }

      await fetchPackages();
      // Reset filter to show all after creating/updating
      setStatusFilter("all");
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
    } catch (error) {
      console.error("Error saving package:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to save package. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm("Are you sure you want to delete this package?")) {
      return;
    }

    try {
      await adminAPI.deletePackage(packageId);
      setMessage({ type: "success", text: "Package deleted successfully" });
      await fetchPackages();
      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 3000);
    } catch (error) {
      console.error("Error deleting package:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to delete package. Please try again.",
      });
      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 5000);
    }
  };

  const columns = [
    { Header: "Name", accessor: "nameEn", width: "20%" },
    { Header: "Price", accessor: "price", width: "10%" },
    { Header: "Type", accessor: "packageType", width: "12%" },
    { Header: "Properties", accessor: "propertiesLabel", width: "18%" },
    { Header: "Dates", accessor: "datesCount", width: "10%" },
    {
      Header: "Status",
      accessor: "isActive",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          label={value ? "Active" : "Inactive"}
          color={value ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      width: "18%",
      Cell: ({ row }) => {
        const pkg = row.original;
        return (
          <MDBox display="flex" gap={1}>
            <IconButton size="small" onClick={() => handleOpenDialog(pkg)} color="info">
              <Icon>edit</Icon>
            </IconButton>
            <IconButton size="small" onClick={() => handleDelete(pkg._id)} color="error">
              <Icon>delete</Icon>
            </IconButton>
          </MDBox>
        );
      },
    },
  ];

  const rows = packages.map((pkg) => {
    const props = pkg.properties || [];
    const propertiesLabel =
      props.length === 0
        ? "—"
        : props
            .map((p) => (typeof p === "object" ? p.title?.en || p.shortId || p._id : p))
            .join(", ");
    return {
      nameEn: pkg.name?.en || "N/A",
      price: `EGP ${pkg.price?.toLocaleString() || 0}`,
      packageType: pkg.packageType || "Custom",
      propertiesLabel:
        propertiesLabel.length > 40 ? propertiesLabel.slice(0, 40) + "…" : propertiesLabel,
      datesCount: pkg.selectedDates?.length || 0,
      isActive: pkg.isActive !== undefined ? pkg.isActive : true,
      _id: pkg._id,
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              Packages Management
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Create and manage property packages with calendar date selection
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => handleOpenDialog()}
            startIcon={<Icon>add</Icon>}
          >
            Create Package
          </MDButton>
        </MDBox>

        {message.type && message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Card>
          {loading ? (
            <MDBox p={6} display="flex" justifyContent="center">
              <CircularProgress />
            </MDBox>
          ) : (
            <DataTable
              table={{ columns, rows }}
              showTotalEntries={true}
              isSorted={false}
              noEndBorder
              entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
              canSearch
            />
          )}
        </Card>

        {/* Create/Edit Package Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
          <DialogTitle>{editingPackage ? "Edit Package" : "Create New Package"}</DialogTitle>
          <DialogContent>
            <MDBox display="flex" flexDirection="column" gap={3} pt={2}>
              {message.type && message.text && (
                <Alert severity={message.type}>{message.text}</Alert>
              )}

              {loadingPackage ? (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={6}
                >
                  <CircularProgress size={48} />
                  <MDTypography variant="body2" color="text" mt={2}>
                    Loading package details…
                  </MDTypography>
                </MDBox>
              ) : (
                <Grid container spacing={2}>
                  {/* Package Name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Package Name *"
                      fullWidth
                      value={formData.nameEn}
                      onChange={(e) => handleInputChange("nameEn", e.target.value)}
                      required
                    />
                  </Grid>

                  {/* Package Type */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Package Type</InputLabel>
                      <Select
                        value={formData.packageType}
                        label="Package Type"
                        onChange={(e) => handleInputChange("packageType", e.target.value)}
                        sx={{ minHeight: 44 }}
                      >
                        <MenuItem value="Weekend">Weekend</MenuItem>
                        <MenuItem value="Holiday">Holiday</MenuItem>
                        <MenuItem value="Seasonal">Seasonal</MenuItem>
                        <MenuItem value="Custom">Custom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                      value={formData.descriptionEn}
                      onChange={(e) => handleInputChange("descriptionEn", e.target.value)}
                    />
                  </Grid>

                  {/* Price */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Price (EGP) *"
                      type="number"
                      fullWidth
                      value={formData.price}
                      onChange={(e) => handleInputChange("price", e.target.value)}
                      required
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>

                  {/* Discount Percentage */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Discount Percentage (%)"
                      type="number"
                      fullWidth
                      value={formData.discountPercentage}
                      onChange={(e) => handleInputChange("discountPercentage", e.target.value)}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                  </Grid>

                  {/* Properties - which properties this package applies to */}
                  <Grid item xs={12}>
                    <FormControl fullWidth disabled={loadingProperties}>
                      <InputLabel>Apply to properties</InputLabel>
                      <Select
                        multiple
                        value={formData.properties || []}
                        label="Apply to properties"
                        onChange={(e) => handleInputChange("properties", e.target.value)}
                        renderValue={(selected) =>
                          selected.length === 0
                            ? "Select properties"
                            : selected
                                .map((id) => propertiesList.find((p) => (p._id || p.id) === id))
                                .filter(Boolean)
                                .map((p) => p.title?.en || p.shortId || p._id || p.id)
                                .join(", ")
                        }
                        sx={{ minHeight: 44 }}
                      >
                        {propertiesList.map((prop) => (
                          <MenuItem key={prop._id || prop.id} value={prop._id || prop.id}>
                            {prop.title?.en || prop.shortId || prop._id || prop.id}
                          </MenuItem>
                        ))}
                        {propertiesList.length === 0 && !loadingProperties && (
                          <MenuItem disabled>No properties found</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                      Leave empty to not link this package to any property yet.
                    </MDTypography>
                  </Grid>

                  {/* Active Status */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e) => handleInputChange("isActive", e.target.checked)}
                          color={formData.isActive ? "success" : "default"}
                        />
                      }
                      label="Package is Active"
                    />
                  </Grid>

                  {/* Calendar for Date Selection */}
                  <Grid item xs={12}>
                    <MDTypography variant="h6" fontWeight="bold" mb={2}>
                      Select Package Dates *
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mb={2}>
                      Click on dates in the calendar to select/deselect them. Selected dates will be
                      highlighted in blue.
                    </MDTypography>
                    <Paper sx={{ p: 2, height: 500 }}>
                      <Calendar
                        localizer={localizer}
                        events={calendarEvents}
                        startAccessor="start"
                        endAccessor="end"
                        defaultDate={calendarDate}
                        defaultView="month"
                        onNavigate={setCalendarDate}
                        onSelectSlot={handleSelectSlot}
                        selectable
                        eventPropGetter={eventStyleGetter}
                        style={{ height: 450 }}
                      />
                    </Paper>
                    <MDBox mt={2}>
                      <MDTypography variant="body2" color="text">
                        Selected Dates: {selectedDates.length} date(s)
                      </MDTypography>
                      {selectedDates.length > 0 && (
                        <MDBox mt={1} display="flex" flexWrap="wrap" gap={1}>
                          {selectedDates.map((date, index) => (
                            <Chip
                              key={index}
                              label={moment(date).format("MMM DD, YYYY")}
                              onDelete={() => {
                                setSelectedDates((prev) => prev.filter((_, i) => i !== index));
                              }}
                              color="info"
                              size="small"
                            />
                          ))}
                        </MDBox>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCloseDialog} disabled={submitting}>
              Cancel
            </MDButton>
            <MDButton
              onClick={handleSave}
              variant="gradient"
              color="info"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <Icon>save</Icon>}
            >
              {submitting ? "Saving..." : editingPackage ? "Update" : "Create"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PackagesManagement;
